import React, { useState, useEffect } from 'react';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { zxcvbn } from '@zxcvbn-ts/core';

export interface ChangePasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
    isLoading?: boolean;
    error?: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isLoading = false,
    error,
}) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }>({});

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
            setValidationErrors({});
        }
    }, [isOpen]);

    const validatePassword = (password: string): string => {
        if (!password) {
            return 'Password is required';
        }

        if (password.length < 8) {
            return 'Password must be at least 8 characters long';
        }

        if (password.length > 128) {
            return 'Password must be less than 128 characters';
        }

        // Check for at least one uppercase letter
        if (!/[A-Z]/.test(password)) {
            return 'Password must contain at least one uppercase letter';
        }

        // Check for at least one lowercase letter
        if (!/[a-z]/.test(password)) {
            return 'Password must contain at least one lowercase letter';
        }

        // Check for at least one number
        if (!/\d/.test(password)) {
            return 'Password must contain at least one number';
        }

        // Check for at least one special character
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
            return 'Password must contain at least one special character';
        }

        return '';
    };

    const validateForm = (): boolean => {
        const errors: typeof validationErrors = {};

        if (!currentPassword) {
            errors.currentPassword = 'Current password is required';
        }

        const newPasswordError = validatePassword(newPassword);
        if (newPasswordError) {
            errors.newPassword = newPasswordError;
        }

        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your new password';
        } else if (newPassword !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        if (currentPassword && newPassword && currentPassword === newPassword) {
            errors.newPassword = 'New password must be different from current password';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentPassword(e.target.value);
        if (validationErrors.currentPassword) {
            setValidationErrors(prev => {
                const { currentPassword, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword(e.target.value);
        if (validationErrors.newPassword) {
            setValidationErrors(prev => {
                const { newPassword, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        if (validationErrors.confirmPassword) {
            setValidationErrors(prev => {
                const { confirmPassword, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            await onSubmit(currentPassword, newPassword);
        } catch (err) {
            // Error handling is managed by parent component
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    // Password strength function matching RegistrationPage exactly
    const getPasswordStrength = (password: string) => {
        if (!password) return { level: 0, entropy: 0 };
        const result = zxcvbn(password);
        const entropy = Math.log2(result.guesses);
        const level = result.score;
        return { level, entropy };
    };

    // Colors and labels matching RegistrationPage exactly
    const red = '#F04438';
    const yellow = '#F8B400';
    const green = '#22C55E';
    const barEmpty = 'rgba(0,0,0,0.12)';
    const greyText = '#8C929C';

    const strengthLabels = [
        { text: 'Very Weak', emoji: '😟' },
        { text: 'Weak', emoji: '😕' },
        { text: 'Strong', emoji: '🙂' },
        { text: 'Very Strong', emoji: '💪' },
    ];

    // Eye icon components
    const EyeIcon = ({ isVisible, hasError }: { isVisible: boolean; hasError: boolean }) => {
        const color = hasError ? '#F04438' : greyText;

        if (isVisible) {
            // Eye with slash (hide password)
            return (
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ cursor: 'pointer' }}
                >
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3-11-8 1.13-2.9 3-5.23 5.32-6.64" />
                    <path d="M3 3l18 18" />
                </svg>
            );
        } else {
            // Regular eye (show password)
            return (
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ cursor: 'pointer' }}
                >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M2.05 12a10.97 10.97 0 0 1 19.9 0 10.97 10.97 0 0 1-19.9 0z" />
                </svg>
            );
        }
    };

    const passwordStrength = getPasswordStrength(newPassword);

    const contentStyles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    };

    const passwordStrengthStyles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '8px',
    };

    const strengthBarContainerStyles: React.CSSProperties = {
        display: 'flex',
        gap: '4px',
        height: '6px',
    };

    const buttonGroupStyles: React.CSSProperties = {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px',
    };

    const isFormValid = currentPassword && newPassword && confirmPassword && Object.keys(validationErrors).length === 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Change Password"
            size="md"
            closeOnOverlayClick={!isLoading}
            closeOnEscape={!isLoading}
        >
            <form onSubmit={handleSubmit} style={contentStyles}>
                <Input
                    label="Current Password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={handleCurrentPasswordChange}
                    placeholder="Enter current password"
                    error={validationErrors.currentPassword || (error && !validationErrors.newPassword && !validationErrors.confirmPassword ? error : '')}
                    disabled={isLoading}
                    required
                    autoFocus
                    rightIcon={
                        <div onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                            <EyeIcon
                                isVisible={showCurrentPassword}
                                hasError={Boolean(validationErrors.currentPassword || (error && !validationErrors.newPassword && !validationErrors.confirmPassword))}
                            />
                        </div>
                    }
                />

                <div>
                    <Input
                        label="New Password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        placeholder="Enter new password"
                        error={validationErrors.newPassword || ''}
                        disabled={isLoading}
                        required
                        rightIcon={
                            <div onClick={() => setShowNewPassword(!showNewPassword)}>
                                <EyeIcon
                                    isVisible={showNewPassword}
                                    hasError={Boolean(validationErrors.newPassword)}
                                />
                            </div>
                        }
                    />
                    {newPassword && (
                        <div style={passwordStrengthStyles}>
                            <div style={strengthBarContainerStyles}>
                                {[0, 1, 2, 3].map(i => {
                                    const strength = passwordStrength.level;
                                    let color = barEmpty;
                                    if (strength === 1 && i === 0) color = red;
                                    if (strength === 2 && i < 2) color = yellow;
                                    if (strength === 3 && i < 3) color = green;
                                    if (strength === 4) color = green;
                                    return (
                                        <div
                                            key={i}
                                            style={{
                                                flex: 1,
                                                height: 6,
                                                borderRadius: 1000,
                                                background: color,
                                            }}
                                        />
                                    );
                                })}
                            </div>
                            {passwordStrength.level > 0 && (
                                <span
                                    style={{
                                        fontSize: 13,
                                        color: greyText,
                                        alignSelf: 'flex-end',
                                    }}
                                >
                                    {strengthLabels[passwordStrength.level - 1]?.text}{' '}
                                    {strengthLabels[passwordStrength.level - 1]?.emoji}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <Input
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    placeholder="Confirm new password"
                    error={validationErrors.confirmPassword || ''}
                    disabled={isLoading}
                    required
                    rightIcon={
                        <div onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                            <EyeIcon
                                isVisible={showConfirmPassword}
                                hasError={Boolean(validationErrors.confirmPassword)}
                            />
                        </div>
                    }
                />

                <div style={buttonGroupStyles}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        isLoading={isLoading}
                        disabled={!isFormValid || isLoading}
                    >
                        Change Password
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default ChangePasswordModal;